<template>
    <div class="custom-dropdown" ref="dropdownRef" @click.stop>
        <div class="custom-dropdown-trigger" @click.stop="toggle">
            <slot name="trigger">
                <i class="fas fa-ellipsis-v"></i>
            </slot>
        </div>
        <Transition name="dropdown">
            <div
                v-show="isOpen"
                ref="menuRef"
                class="custom-dropdown-menu"
                :class="[menuClass, { 'is-portal': appendToBody }, { 'is-moving': isMenuMoving }]"
                :data-align="align"
                :style="appendToBody ? portalStyle : null"
            >
                <div v-for="(item, idx) in visibleItems" :key="idx" class="custom-dropdown-item-wrap">
                    <div
                        class="custom-dropdown-item"
                        :class="[
                            item.variant === 'danger' ? 'item-danger' : 'item-default',
                            { 'has-submenu': item.subItems && item.subItems.length },
                            { 'is-disabled': item.disabled },
                        ]"
                        @click="handleItemClick(item)"
                        @mouseenter="handleItemHover(item, idx)"
                        @mouseleave="handleItemLeave"
                    >
                        <app-icon v-if="item.icon" :icon="item.icon" class="item-icon" />
                        <span class="item-label">{{ item.label }}</span>
                        <i v-if="item.subItems && item.subItems.length" class="fas fa-chevron-right item-chevron"></i>
                    </div>
                    <Transition name="subdropdown">
                        <div
                            v-show="hoveredSubIdx === idx"
                            class="custom-dropdown-submenu"
                            @mouseenter="handleSubmenuEnter(idx)"
                            @mouseleave="handleSubmenuLeave"
                        >
                            <div
                                v-for="(sub, subIdx) in item.subItems || []"
                                :key="subIdx"
                                class="custom-dropdown-subitem"
                                :class="{ 'is-disabled': sub.disabled }"
                                @click.stop="handleSubItemClick(item, sub)"
                            >
                                {{ sub.text || sub.label }}
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
/**
 * CustomDropdown
 * 커스텀 드롭다운 메뉴 컴포넌트
 * - items 기반 메뉴 렌더링 (icon, label, variant)
 * - subItems 있으면 hover 시 우측 서브메뉴
 * - appendToBody: body에 렌더링 (overflow 겹침 방지)
 * - 다중 드롭다운: 한 개만 열림 (custom-dropdown-opened 이벤트)
 */
export default {
    name: "CustomDropdown",
    props: {
        /** 메뉴 항목 [{ icon, label, variant, onClick, visible, disabled, subItems }] */
        items: {
            type: Array,
            default: () => [],
        },
        /** 메뉴 컨테이너 추가 클래스 */
        menuClass: {
            type: [String, Array, Object],
            default: "",
        },
        /** 정렬: 'left' | 'right' (가로) | 'top' | 'bottom' (세로). 기본은 bottom-right */
        align: {
            type: String,
            default: "right",
        },
        /** 메뉴를 body에 렌더링 (부모 overflow 방지) */
        appendToBody: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            instanceId: `dropdown-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            isOpen: false,
            hoveredSubIdx: null,
            hoverTimeout: null,
            portalStyle: null,
            portalWrapper: null,
            menuInPortal: false,
            portalWrapperClickHandler: null,
            justOpened: false,
        };
    },
    computed: {
        /** visible !== false 인 메뉴만 필터 */
        visibleItems() {
            return this.items.filter((i) => i.visible !== false);
        },
        isMenuMoving() {
            return this.appendToBody && !this.menuInPortal;
        },
    },
    watch: {
        isOpen(val) {
            if (this.appendToBody) {
                if (val) {
                    this.menuInPortal = false;
                    this.justOpened = true;
                    this.$nextTick(() => this.moveMenuToBody());
                    this.$nextTick(() => {
                        requestAnimationFrame(() => {
                            this.justOpened = false;
                        });
                    });
                } else {
                    this.moveMenuBack();
                }
            }
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        document.addEventListener("custom-dropdown-opened", this.handleAnotherDropdownOpened);
        if (this.appendToBody) {
            window.addEventListener("scroll", this.updatePortalPosition, true);
            window.addEventListener("resize", this.updatePortalPosition);
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        document.removeEventListener("custom-dropdown-opened", this.handleAnotherDropdownOpened);
        if (this.appendToBody) {
            window.removeEventListener("scroll", this.updatePortalPosition, true);
            window.removeEventListener("resize", this.updatePortalPosition);
        }
        this.moveMenuBack();
    },
    methods: {
        // ========================================
        // 열기/닫기
        // ========================================

        /** 토글 (열면 custom-dropdown-opened 이벤트 dispatch) */
        toggle() {
            const willOpen = !this.isOpen;
            this.isOpen = willOpen;
            if (willOpen) {
                document.dispatchEvent(new CustomEvent("custom-dropdown-opened", { detail: { id: this.instanceId } }));
            }
        },
        /** 드롭다운 닫기 (서브메뉴 hover 상태 초기화 포함) */
        close() {
            this.isOpen = false;
            this.hoveredSubIdx = null;
            this.clearHoverTimeout();
        },

        // ========================================
        // 단일 드롭다운 (다른 드롭다운 열리면 닫힘)
        // ========================================

        /** 다른 드롭다운이 열렸을 때 이 드롭다운 닫기 */
        handleAnotherDropdownOpened(e) {
            if (e.detail?.id !== this.instanceId && this.isOpen) {
                this.close();
            }
        },

        // ========================================
        // 클릭 아웃사이드
        // ========================================

        /** 트리거·메뉴 외부 클릭 시 닫기 */
        handleClickOutside(e) {
            if (this.justOpened) return;
            const triggerEl = this.$refs.dropdownRef;
            const menuEl = this.$refs.menuRef;
            if (!triggerEl) return;
            const inTrigger = triggerEl.contains(e.target);
            const inMenu = menuEl && menuEl.contains && menuEl.contains(e.target);
            const inPortal = this.portalWrapper && this.portalWrapper.contains(e.target);
            if (!inTrigger && !inMenu && !inPortal) {
                this.close();
            }
        },

        // ========================================
        // Portal (body 렌더링)
        // ========================================

        /** portal 메뉴 위치 갱신 (트리거 기준 fixed) */
        updatePortalPosition() {
            if (!this.appendToBody || !this.isOpen) return;
            const trigger = this.$refs.dropdownRef?.querySelector(".custom-dropdown-trigger");
            const menu = this.$refs.menuRef;
            if (!trigger || !menu) return;
            const rect = trigger.getBoundingClientRect();
            const isTop = this.align === "top";
            const isLeft = this.align === "left";
            const style = {
                position: "fixed",
                minWidth: "140px",
            };
            if (isTop) {
                style.bottom = `${window.innerHeight - rect.top + 4}px`;
                style.top = "auto";
            } else {
                style.top = `${rect.bottom + 4}px`;
            }
            if (isLeft) {
                style.left = `${rect.left}px`;
                style.right = "auto";
            } else {
                style.left = "auto";
                style.right = `${window.innerWidth - rect.right}px`;
            }
            this.portalStyle = style;
        },
        /** 메뉴 DOM을 body 최상위 오버레이로 이동 (트리거에 가리지 않도록) */
        moveMenuToBody() {
            const menu = this.$refs.menuRef;
            if (!menu || !this.appendToBody) return;
            if (this.portalWrapper && document.body.contains(this.portalWrapper)) {
                this.menuInPortal = true;
                if (menu) menu.classList.remove("is-moving");
                this.updatePortalPosition();
                return;
            }
            const wrapper = document.createElement("div");
            wrapper.className = "custom-dropdown-portal-root";
            wrapper.setAttribute("data-dropdown-portal", "");
            wrapper.style.cssText =
                "position:fixed;inset:0;z-index:2147483647;pointer-events:auto;";
            wrapper.appendChild(menu);
            document.body.appendChild(wrapper);
            this.portalWrapper = wrapper;
            const self = this;
            const onWrapperClick = function (e) {
                if (menu && !menu.contains(e.target)) {
                    self.close();
                }
            };
            wrapper.addEventListener("click", onWrapperClick);
            this.portalWrapperClickHandler = onWrapperClick;
            menu.style.pointerEvents = "auto";
            this.menuInPortal = true;
            menu.classList.remove("is-moving");
            this.updatePortalPosition();
        },
        /** 메뉴 DOM을 원래 부모로 복귀 */
        moveMenuBack() {
            const menu = this.$refs.menuRef;
            const wrap = this.$refs.dropdownRef;
            if (!menu || !wrap) return;
            this.menuInPortal = false;
            if (menu.style) menu.style.pointerEvents = "";
            if (this.portalWrapper) {
                if (this.portalWrapperClickHandler) {
                    this.portalWrapper.removeEventListener("click", this.portalWrapperClickHandler);
                    this.portalWrapperClickHandler = null;
                }
                if (!wrap.contains(menu)) {
                    wrap.appendChild(menu);
                }
                if (this.portalWrapper.parentNode === document.body) {
                    document.body.removeChild(this.portalWrapper);
                }
                this.portalWrapper = null;
            }
            this.portalStyle = null;
        },

        // ========================================
        // 메뉴 아이템 클릭
        // ========================================

        /** 메인 메뉴 아이템 클릭 (disabled 시 무시, subItems 있으면 무시, onClick만 실행) */
        handleItemClick(item) {
            if (item.disabled) return;
            if (item.subItems && item.subItems.length) return;
            if (typeof item.onClick === "function") {
                item.onClick();
            }
            this.close();
        },
        /** 서브메뉴 아이템 클릭 (sub.disabled 시 무시) */
        handleSubItemClick(item, sub) {
            if (sub.disabled) return;
            if (typeof sub.onClick === "function") {
                sub.onClick();
            } else if (typeof item.onSubItemClick === "function") {
                item.onSubItemClick(sub);
            }
            this.close();
        },

        // ========================================
        // 서브메뉴 hover
        // ========================================

        /** 메인 메뉴 hover 시 서브메뉴 표시 */
        handleItemHover(item, idx) {
            if (item.subItems && item.subItems.length) {
                this.clearHoverTimeout();
                this.hoveredSubIdx = idx;
            }
        },
        /** 메인 메뉴 leave (debounce로 서브메뉴 숨김) */
        handleItemLeave() {
            this.hoverTimeout = setTimeout(() => {
                this.hoveredSubIdx = null;
            }, 100);
        },
        /** 서브메뉴 enter 시 유지 */
        handleSubmenuEnter(idx) {
            this.clearHoverTimeout();
            this.hoveredSubIdx = idx;
        },
        /** 서브메뉴 leave (debounce로 숨김) */
        handleSubmenuLeave() {
            this.hoverTimeout = setTimeout(() => {
                this.hoveredSubIdx = null;
            }, 100);
        },
        /** hover debounce 타이머 정리 */
        clearHoverTimeout() {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
        },
    },
};
</script>

<style scoped>
.custom-dropdown {
    position: relative;
    display: inline-block;
    z-index: 1;
}

.custom-dropdown-trigger {
    position: relative;
    z-index: 0;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
}

.custom-dropdown-trigger:hover {
    color: #64748b;
}

.custom-dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    min-width: 140px;
    padding: 5px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10000;
}

.custom-dropdown-menu.is-portal {
    margin-top: 0;
    z-index: 99999;
}

.custom-dropdown-menu.is-moving {
    visibility: hidden !important;
    pointer-events: none !important;
}

.custom-dropdown-menu[data-align="left"] {
    left: 0;
    right: auto;
}

.custom-dropdown-menu[data-align="top"] {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 4px;
}

.custom-dropdown-menu[data-align="bottom"] {
    top: 100%;
    bottom: auto;
    margin-top: 4px;
    margin-bottom: 0;
}

.custom-dropdown-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    cursor: pointer;
    transition: all 0.15s;
    border-radius: 6px;
}

.custom-dropdown-item .item-icon {
    width: 18px;
    font-size: 12px;
    color: #64748b;
    text-align: center;
    flex-shrink: 0;
}

.custom-dropdown-item .item-label {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    letter-spacing: -0.01em;
}

/* default variant */
.custom-dropdown-item.item-default:hover {
    background: #f1f5f9;
}

.custom-dropdown-item.item-default:hover .item-label {
    color: #1e293b;
    font-weight: 600;
}

.custom-dropdown-item.item-default:hover .item-icon {
    color: #475569;
}

/* disabled: 클릭 불가, 흐린 표시 (확장용) */
.custom-dropdown-item.is-disabled {
    cursor: not-allowed;
    opacity: 0.55;
}
.custom-dropdown-item.is-disabled:hover {
    background: transparent;
}
.custom-dropdown-item.is-disabled:hover .item-label {
    color: #334155;
    font-weight: 500;
}
.custom-dropdown-item.is-disabled:hover .item-icon {
    color: #64748b;
}

.custom-dropdown-item-wrap {
    position: relative;
}

.custom-dropdown-item.has-submenu {
    padding-right: 24px;
}

.custom-dropdown-item .item-chevron {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #94a3b8;
}

.custom-dropdown-submenu {
    position: absolute;
    left: 100%;
    top: 0;
    margin-left: 8px;
    min-width: 120px;
    padding: 5px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1001;
}

.custom-dropdown-subitem {
    padding: 6px 8px;
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s;
}

.custom-dropdown-subitem:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.custom-dropdown-subitem.is-disabled {
    cursor: not-allowed;
    opacity: 0.55;
}
.custom-dropdown-subitem.is-disabled:hover {
    background: transparent;
    color: #334155;
}

.subdropdown-enter-active,
.subdropdown-leave-active {
    transition:
        opacity 0.15s ease,
        transform 0.1s ease;
}

.subdropdown-enter,
.subdropdown-leave-to {
    opacity: 0;
    transform: translateX(-4px);
}

/* danger variant */
.custom-dropdown-item.item-danger:hover {
    background: #fecaca;
}

.custom-dropdown-item.item-danger:hover .item-label {
    color: #dc2626;
    font-weight: 600;
}

.custom-dropdown-item.item-danger:hover .item-icon {
    color: #dc2626;
}

/* 열림/닫힘 애니메이션 */
.dropdown-enter-active {
    animation: dropdown-open 0.2s ease-out;
}

.dropdown-leave-active {
    animation: dropdown-close 0.15s ease-in forwards;
}

@keyframes dropdown-open {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes dropdown-close {
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(-4px);
    }
}
</style>
